import { useCallback, useEffect, useState } from "react";
import { AlarmClock, Plus, Trash2, Pencil, X, ImagePlus, BellOff, Bell, RotateCcw, Link2, ExternalLink, CalendarDays, Plane, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { auth } from "../../lib/firebase";

const PRESETS = [
  [0, "At event time"],
  [60, "1 hour before"],
  [1440, "1 day before"],
  [10080, "1 week before"],
];
const UNIT_MIN = { minutes: 1, hours: 60, days: 1440, weeks: 10080 };
const MAX_UPLOAD = 20 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const EMPTY = { title: "", event_date: "", event_time: "09:00", description: "", links: [], alert_offsets: [1440] };

export function offsetLabel(m) {
  if (m === 0) return "at event time";
  for (const [unit, size] of [["week", 10080], ["day", 1440], ["hour", 60], ["minute", 1]]) {
    if (m % size === 0) { const n = m / size; return `${n} ${unit}${n !== 1 ? "s" : ""} before`; }
  }
  return `${m} min before`;
}

function countdown(eventAt) {
  const diff = new Date(eventAt).getTime() - Date.now();
  if (diff < 0) return "past";
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `in ${mins} min`;
  if (mins < 1440) return `in ${Math.round(mins / 60)} h`;
  return `in ${Math.round(mins / 1440)} day${Math.round(mins / 1440) !== 1 ? "s" : ""}`;
}

/* Auto-shrink large or unsupported images to JPEG before upload (max 20 MB). */
async function compressImage(file) {
  if (ALLOWED_TYPES.includes(file.type) && file.size < 2.5 * 1024 * 1024) return file;
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const scale = Math.min(1, 2200 / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (blob && (blob.size < file.size || !ALLOWED_TYPES.includes(file.type))) {
      return new File([blob], `${(file.name || "photo").replace(/\.\w+$/, "")}.jpg`, { type: "image/jpeg" });
    }
  } catch { /* fall back */ }
  return file;
}

function AlertPicker({ offsets, onChange }) {
  const [customN, setCustomN] = useState("");
  const [customUnit, setCustomUnit] = useState("days");
  const toggle = (m) => onChange(offsets.includes(m) ? offsets.filter((x) => x !== m) : [...offsets, m].sort((a, b) => a - b));
  const addCustom = () => {
    const n = parseInt(customN, 10);
    if (!n || n < 1) return toast.error("Enter a number for the custom alert");
    const m = n * UNIT_MIN[customUnit];
    if (offsets.includes(m)) return toast.error("That alert is already added");
    onChange([...offsets, m].sort((a, b) => a - b));
    setCustomN("");
  };
  return (
    <div data-testid="remindme-alert-picker">
      <div className="text-xs font-700 uppercase tracking-wide text-zinc-500 mb-1.5">Alert me</div>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map(([m, label]) => (
          <button key={m} type="button" onClick={() => toggle(m)} data-testid={`remindme-preset-${m}`}
            className={`px-2.5 py-1 text-xs border font-700 ${offsets.includes(m) ? "bg-amber-100 border-amber-500 text-amber-900" : "border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}>
            {label}
          </button>
        ))}
        {offsets.filter((m) => !PRESETS.some(([p]) => p === m)).map((m) => (
          <span key={m} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border bg-amber-100 border-amber-500 text-amber-900 font-700">
            {offsetLabel(m)}
            <button type="button" onClick={() => toggle(m)} data-testid={`remindme-custom-remove-${m}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input type="number" min="1" value={customN} onChange={(e) => setCustomN(e.target.value)} placeholder="e.g. 3"
          data-testid="remindme-custom-n" className="border border-zinc-300 px-2 py-1.5 text-sm w-20 bg-white" />
        <select value={customUnit} onChange={(e) => setCustomUnit(e.target.value)} data-testid="remindme-custom-unit"
          className="border border-zinc-300 px-2 py-1.5 text-sm bg-white">
          {Object.keys(UNIT_MIN).map((u) => <option key={u} value={u}>{u} before</option>)}
        </select>
        <button type="button" onClick={addCustom} data-testid="remindme-custom-add"
          className="text-xs font-700 text-amber-700 border border-amber-400 px-2.5 py-1.5 hover:bg-amber-50">+ Add custom alert</button>
      </div>
    </div>
  );
}

/* Remind Me — personal topics with date/time, images, links and multi-interval alerts
   (in-app bell + email). Upcoming trips auto-appear and can be silenced or reset. */
export default function RemindMeSection() {
  const [view, setView] = useState("upcoming");
  const [items, setItems] = useState(null);
  const [imgTok, setImgTok] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [newLink, setNewLink] = useState({ title: "", url: "" });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => api.get(`/remind-me?view=${view}`).then((r) => setItems(r.data.items)).catch(() => setItems([])), [view]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { auth.currentUser?.getIdToken().then(setImgTok).catch(() => {}); }, []);

  const startEdit = (t) => {
    setEditing(t.id); setShowForm(true);
    setF({ title: t.title, event_date: t.event_date, event_time: t.event_time || "09:00", description: t.description || "", links: t.links || [], alert_offsets: t.alert_offsets || [] });
  };
  const closeForm = () => { setShowForm(false); setEditing(null); setF(EMPTY); setPhotoFiles([]); setNewLink({ title: "", url: "" }); };

  const addLink = () => {
    let url = newLink.url.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    const norm = (u) => u.replace(/\/+$/, "").toLowerCase();
    if ((f.links || []).some((l) => norm(l.url) === norm(url))) return toast.error("That link is already added");
    setF({ ...f, links: [...(f.links || []), { title: newLink.title.trim(), url }] });
    setNewLink({ title: "", url: "" });
  };

  const uploadPhotos = async (id, files) => {
    let ok = 0;
    for (const src of files) {
      const file = await compressImage(src);
      if (file.size > MAX_UPLOAD) { toast.error(`${src.name}: too large even after shrinking`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      try { await api.post(`/remind-me/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); ok += 1; }
      catch (e) { toast.error(`${src.name}: ${e?.response?.data?.detail || "upload failed"}`); }
    }
    return ok;
  };

  const save = async () => {
    if (!f.title.trim() || !f.event_date) return toast.error("Title and date are required");
    if (!(f.alert_offsets || []).length) return toast.error("Pick at least one alert");
    setSaving(true);
    try {
      const event_at = new Date(`${f.event_date}T${f.event_time || "09:00"}`).toISOString();
      const body = { ...f, event_at };
      let tid = editing;
      if (editing) await api.put(`/remind-me/${editing}`, body);
      else { const r = await api.post("/remind-me", body); tid = r.data.id; }
      if (photoFiles.length) {
        const n = await uploadPhotos(tid, photoFiles);
        toast.success(`Reminder ${editing ? "updated" : "saved"} — ${n}/${photoFiles.length} photo${photoFiles.length > 1 ? "s" : ""} uploaded`);
      } else toast.success(editing ? "Reminder updated" : "Reminder saved — alerts armed");
      closeForm(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try { await api.delete(`/remind-me/${t.id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };
  const silence = async (t) => {
    try { await api.post(`/remind-me/${t.id}/silence`, { silenced: !t.silenced }); toast.success(t.silenced ? "Alerts back on" : "Silenced — no more alerts"); load(); }
    catch { toast.error("Failed"); }
  };
  const reset = async (t) => {
    try { await api.post(`/remind-me/${t.id}/reset`); toast.success("Alerts re-armed"); load(); } catch { toast.error("Failed"); }
  };
  const syncTrips = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/remind-me/sync-trips");
      toast.success(r.data.created ? `${r.data.created} trip reminder${r.data.created > 1 ? "s" : ""} added` : "Trips already synced");
      load();
    } catch { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  return (
    <div data-testid="remindme-section">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h2 className="font-heading font-900 text-amber-600 text-2xl flex items-center gap-2"><AlarmClock className="w-6 h-6" /> Remind Me</h2>
        <div className="ml-auto flex gap-2">
          <button onClick={syncTrips} disabled={syncing} data-testid="remindme-sync-trips"
            className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs font-700 text-zinc-700 hover:border-zinc-500 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Sync my trips
          </button>
          <button onClick={() => (showForm ? closeForm() : setShowForm(true))} data-testid="remindme-add-btn"
            className="inline-flex items-center gap-1.5 bg-amber-600 text-white px-3 py-1.5 text-xs font-700 hover:bg-amber-700">
            {showForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} {showForm ? "Close" : "New reminder"}
          </button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Anything worth remembering — renewals, bookings, birthdays, visa windows. Pick when to be alerted (bell + email). Upcoming trips appear automatically; silence or reset them anytime.</p>

      {showForm && (
        <div className="border border-amber-300 bg-amber-50/40 p-4 mb-6 space-y-3" data-testid="remindme-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title — e.g. Renew passport *"
              data-testid="remindme-form-title" className="sm:col-span-3 border border-zinc-300 px-3 py-2 text-sm bg-white" />
            <input type="date" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })}
              data-testid="remindme-form-date" className="border border-zinc-300 px-3 py-2 text-sm bg-white" />
            <input type="time" value={f.event_time} onChange={(e) => setF({ ...f, event_time: e.target.value })}
              data-testid="remindme-form-time" className="border border-zinc-300 px-3 py-2 text-sm bg-white" />
          </div>
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3}
            placeholder="Description / notes (optional)" data-testid="remindme-form-desc"
            className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white" />
          <AlertPicker offsets={f.alert_offsets || []} onChange={(alert_offsets) => setF({ ...f, alert_offsets })} />
          <div className="space-y-1.5" data-testid="remindme-form-links">
            <div className="text-xs font-700 uppercase tracking-wide text-zinc-500">Links</div>
            {(f.links || []).map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <Link2 className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                <span className="truncate">{l.title || l.url}</span>
                <button type="button" onClick={() => setF({ ...f, links: f.links.filter((_, idx) => idx !== i) })}
                  data-testid={`remindme-form-link-rm-${i}`} className="text-zinc-400 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <input value={newLink.title} onChange={(e) => setNewLink({ ...newLink, title: e.target.value })} placeholder="Link title (optional)"
                data-testid="remindme-form-link-title" className="border border-zinc-300 px-2 py-1.5 text-xs bg-white w-40" />
              <input value={newLink.url} onChange={(e) => setNewLink({ ...newLink, url: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addLink()}
                placeholder="https://…" data-testid="remindme-form-link-url" className="border border-zinc-300 px-2 py-1.5 text-xs bg-white flex-1 min-w-[160px]" />
              <button type="button" onClick={addLink} data-testid="remindme-form-link-add"
                className="text-xs font-700 text-indigo-700 border border-indigo-300 px-2.5 hover:bg-indigo-50">+ Add link</button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-1.5 text-xs font-700 text-zinc-600 border border-zinc-300 px-2.5 py-1.5 cursor-pointer hover:border-zinc-500">
              <ImagePlus className="w-3.5 h-3.5" /> Add images
              <input type="file" accept="image/*" multiple className="hidden" data-testid="remindme-form-photos"
                onChange={(e) => setPhotoFiles((p) => [...p, ...Array.from(e.target.files || [])])} />
            </label>
            {photoFiles.length > 0 && <span className="text-xs text-muted-foreground">{photoFiles.length} image{photoFiles.length > 1 ? "s" : ""} will upload on save (max 20 MB, auto-shrunk)</span>}
            <button onClick={save} disabled={saving} data-testid="remindme-form-save"
              className="ml-auto bg-amber-600 text-white px-5 py-2 text-sm font-700 hover:bg-amber-700 disabled:opacity-50">
              {saving ? "Saving…" : editing ? "Update reminder" : "Save reminder"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {["upcoming", "past", "silenced"].map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`remindme-view-${v}`}
            className={`px-3 py-1.5 text-sm border capitalize ${view === v ? "border-amber-600 bg-amber-50 font-700 text-amber-800" : "border-zinc-300"}`}>{v}</button>
        ))}
      </div>

      {items === null ? <p className="text-sm text-muted-foreground py-6">Loading…</p>
        : items.length === 0 ? <p className="text-sm text-muted-foreground py-6" data-testid="remindme-empty">No {view} reminders. Add one above or sync your trips.</p>
        : (
        <div className="space-y-3">
          {items.map((t) => (
            <div key={t.id} className={`border bg-white p-4 ${t.silenced ? "border-zinc-200 opacity-70" : "border-zinc-300"}`} data-testid={`remindme-item-${t.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                {t.source === "trip" ? <Plane className="w-4 h-4 text-sky-600 -rotate-45 shrink-0" /> : <AlarmClock className="w-4 h-4 text-amber-600 shrink-0" />}
                <span className="font-700 text-sm">{t.title}</span>
                {t.source === "trip" && <span className="text-[10px] font-700 uppercase px-1.5 py-0.5 bg-sky-50 text-sky-700 border border-sky-200">from trip</span>}
                {t.silenced && <span className="text-[10px] font-700 uppercase px-1.5 py-0.5 bg-zinc-100 text-zinc-500 border border-zinc-300">silenced</span>}
                <span className="text-xs text-muted-foreground ml-auto font-mono flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" /> {t.event_date} {t.event_time} · <b className="text-amber-700">{countdown(t.event_at)}</b>
                </span>
              </div>
              {t.description && <p className="text-xs text-zinc-600 mt-1.5 ml-6 whitespace-pre-line">{t.description}</p>}
              {(t.photos || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 ml-6">
                  {t.photos.map((ph) => (
                    <img key={ph.id} src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}${imgTok ? `?auth=${imgTok}` : ""}`}
                      alt={ph.caption || ""} className="w-16 h-16 object-cover border border-zinc-200" />
                  ))}
                </div>
              )}
              {(t.links || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 ml-6" data-testid={`remindme-links-${t.id}`}>
                  {t.links.map((l, i) => (
                    <a key={i} href={l.url} target={l.url.startsWith("http") ? "_blank" : "_self"} rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-700 text-indigo-700 border border-indigo-200 px-2 py-0.5 hover:bg-indigo-50">
                      <ExternalLink className="w-3 h-3" /> {l.title || l.url.replace(/^https?:\/\//, "").slice(0, 40)}
                    </a>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 ml-6 text-xs">
                {(t.alert_offsets || []).map((m) => (
                  <span key={m} className={`px-1.5 py-0.5 border ${(t.alerts_sent || []).includes(m) ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-300"}`}>
                    {offsetLabel(m)}{(t.alerts_sent || []).includes(m) ? " ✓ sent" : ""}
                  </span>
                ))}
                <span className="text-muted-foreground">{t.in_app ? "🔔 In app" : ""}{t.in_app && t.email ? " · " : ""}{t.email ? "✉️ Email" : ""}</span>
                <button onClick={() => silence(t)} data-testid={`remindme-silence-${t.id}`}
                  className={`inline-flex items-center gap-1 font-700 hover:underline ${t.silenced ? "text-emerald-700" : "text-zinc-600"}`}>
                  {t.silenced ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />} {t.silenced ? "Unsilence" : "Silence"}
                </button>
                <button onClick={() => reset(t)} data-testid={`remindme-reset-${t.id}`} className="inline-flex items-center gap-1 font-700 text-sky-700 hover:underline">
                  <RotateCcw className="w-3.5 h-3.5" /> Reset alerts
                </button>
                <button onClick={() => startEdit(t)} data-testid={`remindme-edit-${t.id}`} className="inline-flex items-center gap-1 font-700 text-zinc-600 hover:underline">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => del(t)} data-testid={`remindme-delete-${t.id}`} className="inline-flex items-center gap-1 font-700 text-rose-600 hover:underline">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
